import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  useHistory,
  useLocation,
} from 'react-router-dom'

import {
  ApiResponse,
  ApiResponsePagination,
  get,
} from 'utils/apis/insuranceLounge'

import { getUriWithQueryStringValue } from 'utils/string'

import { Post } from 'screens/Blog/types'

import { ContainerProps } from './types'

import BlogPostsGrid from './index.presentational'

const postsPerPage = 4

const BlogPostsGridContainer: React.FC<ContainerProps> = ({
  exceptPostSlug,
  onPostsLoaded,
  tagSlugs,
}): React.ReactElement => {
  const history = useHistory()
  const location = useLocation()

  const lastSlugRegex = /^.*[&?]{1}lastPost=([^&]*).*$/
  const queryStringLastSlug = (location.search.match(lastSlugRegex) && location.search.replace(lastSlugRegex, '$1')) || undefined
  const [
    lastSlugFetched,
    setLastSlugFetched,
  ] = useState<string | undefined>(queryStringLastSlug)

  const [
    isLoading,
    setIsLoading,
  ] = useState<boolean>(true)

  const [
    posts,
    setPosts,
  ] = useState<Post[]>()

  const [
    pagination,
    setPagination,
  ] = useState<ApiResponsePagination | undefined>()

  const tagSlugsRef = useRef({ tagSlugs }).current

  const fetchPosts = useCallback((startingAfter?: string, endingAt?: string, max?: number|false) => {
    const url = `/blog/posts?${max !== false ? `&max=${postsPerPage}` : ''}${tagSlugs ? `&tags=${tagSlugs}` : ''}${startingAfter ? `&starting_after=${startingAfter}` : ''}${endingAt ? `&ending_at=${endingAt}` : ''}${exceptPostSlug ? `&except=${exceptPostSlug}` : ''}`

    return get(url)
      .then((response: ApiResponse): {
        posts?: Post[],
        pagination?: ApiResponsePagination,
      } => {
        const posts = response.status === 200 ? response.data as Post[] : undefined
        const pagination = response.status === 200 ? (response.pagination as ApiResponsePagination) : undefined

        return {
          posts,
          pagination,
        }
      })
  }, [
    exceptPostSlug,
    tagSlugs,
  ])

  // Handle initial load
  useEffect(() => {
    let endingAt = queryStringLastSlug

    if (tagSlugs !== tagSlugsRef.tagSlugs) {
      const newUri = getUriWithQueryStringValue(location, 'lastPost', undefined)
      history.replace(newUri)

      endingAt = undefined

      tagSlugsRef.tagSlugs = tagSlugs
    }

    setIsLoading(true)
    setPosts(undefined)
    setPagination(undefined)
    setLastSlugFetched(endingAt)

    fetchPosts(undefined, endingAt, endingAt ? false : undefined)
      .then(({
        posts,
        pagination,
      }) => {
        setPosts(prevPosts => [
          ...(prevPosts || []),
          ...(posts || []),
        ])
        setPagination(pagination)
        setLastSlugFetched(posts ? posts[posts.length - 1]?.slug : undefined)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [ tagSlugs ]) // eslint-disable-line react-hooks/exhaustive-deps

  // Handle "View More" click
  const onClickViewMore = useCallback(() => {
    setIsLoading(true)
    fetchPosts(lastSlugFetched)
      .then(({
        posts,
        pagination,
      }) => {
        setPosts(prevPosts => [
          ...(prevPosts || []),
          ...(posts || []),
        ])
        setPagination(pagination)

        const lastPostSlug = posts ? posts[posts.length - 1]?.slug : undefined
        setLastSlugFetched(lastPostSlug)

        const newUri = getUriWithQueryStringValue(location, 'lastPost', lastPostSlug)
        history.push(newUri)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [
    fetchPosts,
    history,
    lastSlugFetched,
    location,
  ])

  if (onPostsLoaded) {
    onPostsLoaded(posts)
  }

  return React.createElement(
    BlogPostsGrid,
    {
      isLoading,
      onClickViewMore,
      posts,
      pagination,
    },
    null,
  )
}

export default BlogPostsGridContainer
